import * as React from 'react'

import { XIcon } from 'lucide-react'
import { MobXProviderContext, observer } from 'mobx-react'

import type {
  IStruxTagGpsPathQuery,
  IStruxTagGpsPathQueryVariables,
} from '~/client/graph/operations/generated/StruxTagGpsPath.generated'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '~/client/src/shared/components/shadcn-ui/sheet'
import { useSidePanelOverlayRoot } from '~/client/src/shared/components/SidePanel/SidePanelOverlayContext'
import StruxTagHistoryStore from '~/client/src/shared/stores/domain/StruxTagHistory.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import Localization from '../../localization/LocalizationManager'
import { Loader } from '../Loader'
import StruxTagGpsPathMap from './StruxTagGpsPathMap'

interface IProps {
  isOpen: boolean
  projectId?: string
  infuseId?: string | null
  startedAt?: number | null
  endedAt?: number | null
  onClose(): void
}

interface IMobXContext {
  projectDateStore?: ProjectDateStore
  struxTagHistoryStore: StruxTagHistoryStore
}

type GpsPath = NonNullable<IStruxTagGpsPathQuery['struxTagGpsPath']>
type Summary = GpsPath['summary']
type PathSegment = GpsPath['segments'][number]

const METERS_TO_FEET = 3.28084
const METERS_TO_MILES = 0.000621371
const MPS_TO_MPH = 2.23694

const formatDistance = (meters?: number | null): string => {
  if (!meters) return '0 ft'

  const feet = meters * METERS_TO_FEET
  if (feet < 5280) return `${Math.round(feet).toLocaleString()} ft`

  return `${(meters * METERS_TO_MILES).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })} mi`
}

const formatSpeed = (metersPerSecond?: number | null): string => {
  if (!metersPerSecond) return '0 mph'

  return `${(metersPerSecond * MPS_TO_MPH).toLocaleString(undefined, {
    maximumFractionDigits: 1,
  })} mph`
}

const toDisplayDate = (
  value: unknown,
  projectDateStore?: ProjectDateStore,
): string => {
  if (typeof value === 'number') {
    return (
      projectDateStore?.getMonthDayYearAndTimeToDisplay(value) || String(value)
    )
  }

  if (typeof value === 'string') {
    const timestamp = Date.parse(value)
    if (!Number.isNaN(timestamp)) {
      return (
        projectDateStore?.getMonthDayYearAndTimeToDisplay(timestamp) || value
      )
    }

    return value
  }

  return '-'
}

const StruxTagGpsSummarySheet: React.FC<IProps> = ({
  isOpen,
  projectId,
  infuseId,
  startedAt,
  endedAt,
  onClose,
}) => {
  const { projectDateStore, struxTagHistoryStore } = React.useContext(
    MobXProviderContext,
  ) as IMobXContext
  const overlayRootRef = useSidePanelOverlayRoot()
  const canQuery = !!projectId && !!infuseId && !!startedAt && !!endedAt
  const queryVariables: IStruxTagGpsPathQueryVariables = {
    projectId: projectId || '',
    infuseId: infuseId || '',
    from: startedAt ? new Date(startedAt).toISOString() : '',
    to: endedAt ? new Date(endedAt).toISOString() : '',
    maxPoints: 1000,
  }
  const historyState = struxTagHistoryStore.gpsPathFor(queryVariables)

  React.useEffect(() => {
    if (!isOpen || !canQuery) return
    void struxTagHistoryStore.ensureGpsPath(queryVariables)
  }, [
    canQuery,
    isOpen,
    queryVariables.from,
    queryVariables.infuseId,
    queryVariables.projectId,
    queryVariables.to,
    struxTagHistoryStore,
  ])

  const data = historyState._tag === 'Ready' ? historyState.data : null
  const loading =
    isOpen &&
    canQuery &&
    (historyState._tag === 'NotAsked' || historyState._tag === 'Loading')
  const error = historyState._tag === 'Failed' ? historyState.error : null
  const gpsPath = data?.struxTagGpsPath || null
  const summary = gpsPath?.summary || null
  const pathSegments = React.useMemo(() => {
    return gpsPath?.segments || []
  }, [gpsPath])

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        portalContainer={overlayRootRef?.current}
        overlayClassName="tw:absolute tw:pointer-events-auto"
        className="tw:pointer-events-auto tw:absolute tw:inset-0 tw:rounded-none tw:border-0 tw:shadow-none"
      >
        <SheetHeader className="tw:relative tw:items-center tw:pt-6">
          <SheetClose
            render={
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="tw:absolute tw:left-3 tw:top-5"
              />
            }
          >
            <XIcon />
            <span className="tw:sr-only">Close</span>
          </SheetClose>
          <SheetTitle className="tw:text-title-sm">GPS Summary</SheetTitle>
          <SheetDescription>
            {startedAt
              ? toDisplayDate(startedAt, projectDateStore)
              : Localization.translator.activitySessionOpen}
          </SheetDescription>
        </SheetHeader>
        <div className="tw:flex tw:flex-col tw:gap-6 tw:p-4 tw:pt-0">
          {loading && <Loader />}
          {error && (
            <div className="tw:text-body-md tw:text-destructive">
              {Localization.translator.somethingWentWrongTryAgainLater}
            </div>
          )}
          {!loading && !error && summary && (
            <SummaryContent
              summary={summary}
              pathSegments={pathSegments}
              projectDateStore={projectDateStore}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

const SummaryContent: React.FC<{
  summary: Summary
  pathSegments: PathSegment[]
  projectDateStore?: ProjectDateStore
}> = ({ summary, pathSegments, projectDateStore }) => {
  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      <StruxTagGpsPathMap segments={pathSegments} />

      <div className="tw:border-0 tw:border-b tw:border-solid tw:border-border tw:pb-6">
        <div className="tw:text-title-md tw:text-foreground">
          {formatDistance(summary.distanceMeters)}
        </div>
        <div className="tw:mt-1 tw:text-body-md tw:text-muted-foreground">
          Distance traveled
        </div>
      </div>

      <div className="tw:flex tw:flex-col tw:gap-3">
        <SummaryRow
          label="Average speed"
          value={formatSpeed(summary.averageSpeedMps)}
        />
        <SummaryRow
          label="Max speed"
          value={formatSpeed(summary.maxSpeedMps)}
        />
        <SummaryRow
          label="GPS points"
          value={summary.pointCount.toLocaleString()}
        />
        <SummaryRow
          label="Started"
          value={toDisplayDate(summary.startedAt, projectDateStore)}
        />
        <SummaryRow
          label="Ended"
          value={toDisplayDate(summary.endedAt, projectDateStore)}
        />
      </div>
    </div>
  )
}

const SummaryRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  return (
    <div className="tw:flex tw:items-start tw:justify-between tw:gap-4 tw:text-body-md">
      <div className="tw:text-muted-foreground">{label}</div>
      <div className="tw:text-right tw:text-foreground">{value}</div>
    </div>
  )
}

export default observer(StruxTagGpsSummarySheet)
